import type { ReactNode } from 'react'
import { useMemo } from 'react'

import type { EmotionCache } from '@emotion/cache'
import {
  CacheProvider,
  ThemeProvider as EmotionThemeProvider,
} from '@emotion/react'

import type { Brand, ThemeConfig, ThemeModes } from '@twistopayments/design-system/theme'
import { getTheme } from '@twistopayments/design-system/theme'

import type { DesignSystemContextValue } from './ds-context'
import { DesignSystemContextProvider } from './ds-context'

/**
 * Public design-system entry point — the only thing host apps
 * (web-app B2C, b2b-web-app) need to wrap their root with.
 *
 * Composes:
 *   - `@emotion/react` `CacheProvider` (optional, for SSR / multi-root)
 *   - `@emotion/react` `ThemeProvider` (resolves brand `themeConfig`
 *     through `getTheme` so colour / size / radius / font tokens land
 *     on the same emotion theme object every DS component reads)
 *   - `DesignSystemContextProvider` (host-injected runtime context —
 *     currencyCode, callingCodes, formattedOptions, supportEmail,
 *     support, LinkComponent, ImageComponent)
 *
 * Brand theming is driven by `themeConfig`. Each host ships a single
 * brand object: `b2cTheme`, `b2bTheme`, etc.
 *
 * @example
 * ```tsx
 * // web-app/_app.tsx
 * import { DesignSystemProvider } from '@twistopayments/design-system/base'
 * import NextLink from 'next/link'
 * import NextImage from 'next/image'
 * import { b2cTheme } from './brand/b2c-theme'
 *
 * <DesignSystemProvider
 *   themeConfig={b2cTheme}
 *   currencyCode="CZK"
 *   callingCodes={['+420', '+421']}
 *   formattedOptions={cleavePresets}
 *   LinkComponent={NextLink}
 *   ImageComponent={NextImage}
 *   support={{ email: 'support@…', phoneNumber: '…', hoursFrom: 9,
 *              hoursTo: 18, days: 'mo-fr' }}>
 *   <YourApp />
 * </DesignSystemProvider>
 * ```
 */
export type DesignSystemProviderProps = DesignSystemContextValue & {
  children: ReactNode
  /**
   * Brand overrides (colours, sizes, radii, fontFamily, shadows). All
   * fields are optional — anything you don't supply falls back to the
   * built-in Param defaults.
   */
  themeConfig?: ThemeConfig
  /** Light / dark — defaults to light. */
  themeMode?: ThemeModes
  /**
   * Preset selecting foundations and presentation: 'b2c' (default), 'b2b',
   * or 'b2c-legacy' for the pre-migration B2C appearance.
   * b2b swaps in the grayscale primary/tertiary + teal accent palette;
   * `themeConfig.palette` still merges on top.
   */
  brand?: Brand
  /**
   * Optional emotion cache. Pass one for SSR (so style insertion
   * happens at the right point), or to scope styles to a portal /
   * iframe root. Skip in normal client-side setups.
   */
  emotionCache?: EmotionCache
}

export const DesignSystemProvider = ({
  children,
  themeConfig,
  themeMode,
  brand,
  emotionCache,
  currencyCode,
  currencyLocale,
  onErrorModalOpen,
  callingCodes,
  formattedOptions,
  supportEmail,
  support,
  LinkComponent,
  ImageComponent,
}: DesignSystemProviderProps) => {
  const theme = useMemo(
    () => getTheme({ mode: themeMode, brand, config: themeConfig }),
    [themeMode, brand, themeConfig]
  )

  const contextValue: DesignSystemContextValue = useMemo(
    () => ({
      currencyCode,
      currencyLocale,
      onErrorModalOpen,
      callingCodes,
      formattedOptions,
      supportEmail,
      support,
      LinkComponent,
      ImageComponent,
    }),
    [
      currencyCode,
      currencyLocale,
      onErrorModalOpen,
      callingCodes,
      formattedOptions,
      supportEmail,
      support,
      LinkComponent,
      ImageComponent,
    ]
  )

  const inner = (
    <EmotionThemeProvider theme={theme}>
      <DesignSystemContextProvider value={contextValue}>
        {children}
      </DesignSystemContextProvider>
    </EmotionThemeProvider>
  )

  return emotionCache ? (
    <CacheProvider value={emotionCache}>{inner}</CacheProvider>
  ) : (
    inner
  )
}
